/**
 * Seitenzustände einer Datenkarte (Kalender-Bereich): lädt als Platzhalterzeilen, Ladefehler mit
 * Grund und „Erneut laden“, veraltet nach misslungenem Nachladen. Den Grund formuliert der
 * Aufrufer mit `problemText` – die Bausteine kennen keine API.
 */
import { TriangleAlert } from 'lucide-react';
import { Button } from './Button';

/** Laden: Platzhalterzeilen in Rasterform statt einer leeren Fläche oder endlosem „Lade …“. */
export function SurfaceLoading({ label, rows = 5 }: { label: string; rows?: number }) {
  return (
    <div role="status" aria-label={label} className="animate-pulse">
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="border-card-alt flex h-[58px] items-center gap-4 border-b px-[18px]"
        >
          <span className="bg-card-alt h-3.5 w-24 rounded-sm" />
          <span className="bg-card-alt h-[34px] grow rounded-md" />
        </div>
      ))}
    </div>
  );
}

/**
 * Ladefehler mit dem Grund als deutschem Satz, nie „keine Daten“: was nicht geladen wurde, heißt
 * so, und „Erneut laden“ ist die eine nächste Aktion.
 */
export function SurfaceLoadError({
  message,
  onRetry,
  retrying,
}: {
  message: string;
  /** Ohne Wiederholen, wenn ein neuer Versuch dieselbe Antwort brächte (fehlendes Recht). */
  onRetry?: () => void;
  retrying: boolean;
}) {
  return (
    <div role="alert" className="flex flex-wrap items-center gap-3 p-5">
      <TriangleAlert size={18} aria-hidden className="text-amber flex-none" />
      <p className="min-w-0 flex-1 text-sm">{message}</p>
      {onRetry && (
        <Button variant="secondary" className="min-h-11" onClick={onRetry} loading={retrying}>
          Erneut laden
        </Button>
      )}
    </div>
  );
}

/** Scheitert nur das Nachladen, bleibt der Stand stehen und ein Satz sagt, welcher Stand das ist. */
export function SurfaceStaleHint({
  onRetry,
  retrying,
}: {
  onRetry: () => void;
  retrying: boolean;
}) {
  return (
    <div
      role="status"
      className="bg-amber-wash text-amber-ink border-hairline flex flex-wrap items-center gap-3 border-b px-[18px] py-2 text-[13px]"
    >
      <span className="min-w-0 flex-1">
        Nachladen fehlgeschlagen – zu sehen ist der zuletzt geladene Stand.
      </span>
      <Button variant="ghost" className="min-h-11" onClick={onRetry} loading={retrying}>
        Erneut laden
      </Button>
    </div>
  );
}
